// local-dev-environment Phase 2 remediation (finding #5): route through the
// authClient seam (cognito | local via VITE_AUTH_MODE) instead of calling
// `aws-amplify/auth` directly.
import { authClient } from '../auth/authClient.js';
import type { ZodType } from 'zod';
import { getActiveTraceparent, TRACEPARENT_HEADER } from '../lib/tracing.js';
import { ApiError, type ApiErrorBody } from './errors';

const BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export const SESSION_EXPIRED_EVENT = 'banyan:session-expired';

export interface ApiClientOptions<T = unknown> {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  schema?: ZodType<T>;
}

interface InternalRequestOptions {
  method: string;
  body?: BodyInit | null;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  schema?: ZodType<unknown>;
}

async function getAuthToken(): Promise<string | null> {
  const session = await authClient.fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? null;
}

async function getRefreshedAuthToken(): Promise<string | null> {
  const session = await authClient.fetchAuthSession({ forceRefresh: true });
  return session.tokens?.idToken?.toString() ?? null;
}

function readTraceId(response: Response): string | null {
  return (
    response.headers.get('x-trace-id') ??
    response.headers.get('x-amzn-trace-id') ??
    null
  );
}

async function request(
  path: string,
  options: InternalRequestOptions,
  _isRetry = false
): Promise<unknown> {
  const token = await getAuthToken();
  const method = options.method.toUpperCase();
  const traceparent = getActiveTraceparent();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(traceparent ? { [TRACEPARENT_HEADER]: traceparent } : {}),
    ...options.headers,
  };

  // Explicitly forward only the fetch-relevant fields. `signal` propagation is
  // load-bearing for TanStack Query (Phase 1 of TASK-079) — it lets unmount,
  // dependency change, and `queryClient.cancelQueries(...)` abort the
  // in-flight request, replacing the 21 hand-rolled `let cancelled = false`
  // flags the audit flagged.
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    body: options.body,
    signal: options.signal,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401 && !_isRetry) {
      try {
        const freshToken = await getRefreshedAuthToken();
        if (freshToken) {
          return request(path, options, true);
        }
      } catch {
        // Refresh failed — fall through to session-expired handling
      }
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      throw ApiError.fromSessionExpired();
    }

    let errorBody: ApiErrorBody;
    try {
      errorBody = (await response.json()) as ApiErrorBody;
    } catch {
      errorBody = { message: response.statusText };
    }
    throw ApiError.fromResponse(response, errorBody, readTraceId(response));
  }

  if (response.status === 204) {
    return null;
  }

  const json = (await response.json()) as unknown;

  if (options.schema) {
    const parsed = options.schema.safeParse(json);
    if (!parsed.success) {
      // Surface the first issue path + code so future shape drift is visible
      // in the console instead of just rendering as a generic "did not match
      // expected shape" banner. See `frontend/src/api/schemas/initiatives.ts`
      // for the arrImpactType array drift that prompted this.
      const firstIssue = parsed.error.issues[0];
      // eslint-disable-next-line no-console
      console.warn(
        `[apiClient] ${path} response failed schema validation`,
        {
          path: firstIssue?.path?.join('.'),
          code: firstIssue?.code,
          message: firstIssue?.message,
          totalIssues: parsed.error.issues.length,
        }
      );
      throw ApiError.fromZodFailure(
        path,
        parsed.error.issues,
        readTraceId(response)
      );
    }
    return parsed.data;
  }

  return json;
}

function pickInternal(
  method: string,
  body: BodyInit | null | undefined,
  options: ApiClientOptions<unknown> | undefined
): InternalRequestOptions {
  return {
    method,
    body,
    signal: options?.signal,
    headers: options?.headers,
    schema: options?.schema,
  };
}

/**
 * Centralized API client. All TanStack Query queryFn / mutationFn invocations
 * MUST go through this client — never bypass it.
 *
 * Phase 3 (TASK-079) adds an optional `schema` field on `options`. When set,
 * the response is run through `schema.safeParse(...)` and a `SCHEMA_VALIDATION_FAILED`
 * `ApiError` is thrown on a parse miss. Otherwise the raw JSON is returned and
 * callers see the legacy `Promise<unknown>` shape unchanged.
 */
export interface ApiClient {
  get<T>(path: string, options: ApiClientOptions<T> & { schema: ZodType<T> }): Promise<T>;
  get(path: string, options?: ApiClientOptions): Promise<unknown>;
  post<T>(
    path: string,
    body: unknown,
    options: ApiClientOptions<T> & { schema: ZodType<T> }
  ): Promise<T>;
  post(path: string, body: unknown, options?: ApiClientOptions): Promise<unknown>;
  put<T>(
    path: string,
    body: unknown,
    options: ApiClientOptions<T> & { schema: ZodType<T> }
  ): Promise<T>;
  put(path: string, body: unknown, options?: ApiClientOptions): Promise<unknown>;
  patch<T>(
    path: string,
    body: unknown,
    options: ApiClientOptions<T> & { schema: ZodType<T> }
  ): Promise<T>;
  patch(path: string, body: unknown, options?: ApiClientOptions): Promise<unknown>;
  delete<T>(
    path: string,
    options: ApiClientOptions<T> & { schema: ZodType<T> }
  ): Promise<T>;
  delete(path: string, options?: ApiClientOptions): Promise<unknown>;
}

function bodyToJsonString(body: unknown): string {
  return JSON.stringify(body);
}

export const apiClient: ApiClient = {
  get(path: string, options?: ApiClientOptions<unknown>) {
    return request(path, pickInternal('GET', undefined, options));
  },
  post(path: string, body: unknown, options?: ApiClientOptions<unknown>) {
    return request(path, pickInternal('POST', bodyToJsonString(body), options));
  },
  put(path: string, body: unknown, options?: ApiClientOptions<unknown>) {
    return request(path, pickInternal('PUT', bodyToJsonString(body), options));
  },
  patch(path: string, body: unknown, options?: ApiClientOptions<unknown>) {
    return request(path, pickInternal('PATCH', bodyToJsonString(body), options));
  },
  delete(path: string, options?: ApiClientOptions<unknown>) {
    return request(path, pickInternal('DELETE', undefined, options));
  },
};

export default apiClient;
